import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useRouter } from 'next/router';
import { sessionStore } from 'src/common/utils/storageWrapper';
import captureException from 'src/common/utils/captureException';
import { BOOK_NOW_STATES, EXTERNAL_PATHS, PATHS } from 'src/common/constants';
import type { BookNowFormStateSubmit } from 'src/book-now-review/type';
import type { CheckoutType } from 'src/checkout/types';
import { patchInstabook } from 'src/book-now-review/helpers';
import { useUserContext } from 'src/common/providers/UserProvider';
import type { Trip } from 'src/common/types/api/v4/trips';
import { PARAM_KEYS } from 'src/book-now-review/constants';
import { compressText } from 'src/common/helpers';
import useFeatureFlag from 'src/common/hooks/useFeatureFlag';

export type CheckoutStep = 'add-ons' | 'book-now' | 'register' | 'insurance' | 'payment';

export type SessionData = {
  formData?: BookNowFormStateSubmit;
  availabilityId?: number;
  tripId?: number;
  redirectBack: boolean;
  [key: string]: any;
};

export type ProceedToCheckoutResult = {
  success: boolean;
  sessionKey: string | null;
  error?: {
    message: string;
    code?: string;
    status?: number;
  };
};

export type ProceedToCheckoutStepOptions = {
  checkoutType: CheckoutType;
  /** Form values to store in session storage */
  formData?: BookNowFormStateSubmit;
  /** Which checkout step to redirect to */
  step: CheckoutStep;
  /** Optional tracking function to call before redirect */
  trackingEvent?: () => void;
  /** The availability (instabook) ID */
  availabilityId?: number;
  /** The trip Id. Will override optional pk query param */
  tripPk?: number;
};

/**
 * Custom hook that provides a function to proceed to different checkout steps
 * by storing form data in session storage and redirecting with appropriate navigation method
 *
 * @returns A function that takes options and returns the session key if successful
 */
export const useProceedToCheckoutStep = (): (
(options: ProceedToCheckoutStepOptions) => Promise<ProceedToCheckoutResult>
) => {
  const router = useRouter();
  const { user } = useUserContext();
  const isAuthMigrationEnabled = useFeatureFlag('auth-migration');

  /**
   * Proceed to a checkout step with the provided options
   *
   * @param options Configuration options
   * @returns The session key if successful, null if an error occurred
   */
  const proceedToCheckout = useCallback(async ({
    availabilityId,
    tripPk,
    checkoutType,
    formData,
    step,
    trackingEvent,
  }: ProceedToCheckoutStepOptions): Promise<ProceedToCheckoutResult> => {
    const isInstabookV1Flow = checkoutType === 'instabook';
    const isInstabookV2Flow = checkoutType === 'trip-cart--instabook';
    const sessionData: SessionData = {
      availabilityId,
      formData,
      redirectBack: false,
    };
    const listingId = router.query.listing_id as string;
    let tripId = tripPk || router.query.pk as string;
    let trip: Trip | null = null;
    const listingUrl = `${PATHS.LISTING}${listingId}`;

    try {
      const key = router.query.sessionKey as string || uuidv4();
      if (router.pathname === '/trips/[listing_id]/book-now') {
        sessionStore.setItem(key, JSON.stringify(sessionData));
      }

      // In Trip Cart flow, the owner message is compressed to avoid session storage size limit
      if (isInstabookV2Flow) {
        const { formData: tripCartFormData } = sessionData;

        sessionStore.setItem(key, JSON.stringify({
          ...sessionData,
          formData: {
            ...tripCartFormData,
            ownerMessage: compressText(tripCartFormData?.ownerMessage ?? ''),
          },
        }));
      }
      // Get the current URL search params
      const searchParams = new URLSearchParams(window.location.search);
      // Create parameters for navigation and return URL
      const navParams = new URLSearchParams();

      // Handle instabook flows based on feature flags and user login status
      if (isInstabookV1Flow || isInstabookV2Flow) {
        // Only copy specific allowed parameters
        const allowedParams = Object.values(PARAM_KEYS);
        allowedParams.forEach((param) => {
          if (searchParams.has(param)) {
            navParams.set(param, (searchParams.get(param)!));
          }
        });
        // Add the session key to navigation params
        navParams.set(PARAM_KEYS.SESSION_KEY, key);
        // Set availabilityId for instabook flow
        navParams.set('availabilityId', availabilityId?.toString() ?? '');

        if (user?.id && formData) {
          // This executes on /trips/<listing_id>/book-now/ page submit only
          try {
            trip = await patchInstabook(Number(availabilityId), formData);
            if (
              // Only update tripId when trip.pk available
              trip?.pk
              // prevent calling checkout-session if trip is confirmed or canceled (expired)
              && BOOK_NOW_STATES.includes(trip.state.state)
            ) {
              tripId = trip.pk;
              navParams.set('tripPk', trip.pk.toString());
            }
          } catch (error) {
            const errorMessage = `Failed to patch instabook: ${error}`;
            captureException(errorMessage);
            // Return structured error
            return {
              success: false,
              sessionKey: null,
              error: {
                message: errorMessage,
                status: error?.status ?? null,
                code: 'PATCH_INSTABOOK_FAILED',
              },
            };
          }
        }
      }

      const pathPrefix = `${listingUrl}/checkout/${tripId}/`;
      const bookNowReturnPath = isInstabookV2Flow ? `${listingUrl}` : `${listingUrl}/book-now/`;
      const returnPath = (isInstabookV1Flow || isInstabookV2Flow)
        ? bookNowReturnPath
        : `${EXTERNAL_PATHS.INBOX}${tripId}/`;
      const returnParams = new URLSearchParams();
      returnParams.set(PARAM_KEYS.SESSION_KEY, key);
      if (!isInstabookV2Flow) {
        returnParams.set(PARAM_KEYS.DATE, searchParams.get(PARAM_KEYS.DATE) || '');
        returnParams.set(PARAM_KEYS.DURATION, searchParams.get(PARAM_KEYS.DURATION) || '');
        returnParams.set(PARAM_KEYS.CAPACITY, searchParams.get(PARAM_KEYS.CAPACITY) || '');
        returnParams.set(PARAM_KEYS.CAPTAIN_OPTION, searchParams.get(PARAM_KEYS.CAPTAIN_OPTION) || '');
      }
      if (isInstabookV2Flow) {
        returnParams.set('sheet', '');
      }
      navParams.set('return', `${returnPath}?${returnParams.toString()}`);

      // Call the tracking function if provided
      if (trackingEvent) {
        trackingEvent();
      }

      // Determine the target URL and navigation method based on the step
      switch (step) {
        case 'add-ons': {
          // Add-ons uses client-side navigation
          router.push(
            {
              pathname: `${pathPrefix}add-ons/`,
              query: Object.fromEntries(navParams.entries()),
            },
            undefined,
            { shallow: true },
          );
          break;
        }
        case 'insurance': {
          // Insurance uses client-side navigation
          router.push(
            {
              pathname: `${pathPrefix}insurance/`,
              query: Object.fromEntries(navParams.entries()),
            },
            undefined,
            { shallow: true },
          );
          break;
        }
        case 'register': {
          navParams.set('next', `${listingUrl}${PATHS.BOOK_NOW_SUBMIT}?${returnParams.toString()}`);
          if (isAuthMigrationEnabled) {
            window.location.href = `${PATHS.REGISTER}?${navParams.toString()}`;
          } else {
            window.location.href = `/instabook/${listingId}/contact/?${navParams.toString()}`;
          }
          break;
        }
        default: {
          // Handle unexpected step values
          captureException(`Unexpected checkout step: ${step}`);
          // Fall back to add-ons as a safe default
          router.push({
            pathname: `${pathPrefix}add-ons/`,
            query: Object.fromEntries(navParams.entries()),
          });
          break;
        }
      }

      return {
        success: true,
        sessionKey: key,
      };
    } catch (error) {
      captureException(`Failed to proceed to checkout step (${step}): ${error}`);
      return {
        success: false,
        sessionKey: null,
        error: {
          message: `Failed to proceed to checkout step (${step}): ${error}`,
          code: 'GENERAL_CHECKOUT_ERROR',
        },
      };
    }
  }, [router, user?.id, isAuthMigrationEnabled]);

  return proceedToCheckout;
};
